// NodeValueRow — the numeric control inside a graph node (ENG-1121).
//
// Generalised from RigChainNodes' own ValueRow, which already had the drag fix
// and the reasoning behind it; Blend and LayeredBoneBlend had neither, so the
// same control behaved differently depending on which node you were looking at.
//
// Two halves, and each exists for a stated reason:
//
//   THE SLIDER carries `nodrag` (see nodeControlClass.js). Without it the drag
//   is swallowed by xyflow's d3-drag listener and the NODE moves instead of the
//   value — the reported bug, where the slider "only jumps to the clicked
//   position": the click landed, the drag became a node drag.
//
//   THE ENTRY FIELD is `type="text"` + inputMode, never `type="number"`. The
//   native spinner auto-repeats while held and stops on mouseup, and a node
//   re-rendering on the editor's 100ms debug poll can lose that mouseup, which
//   reads as the value running away on its own. A text field has no spinner and
//   still takes a typed value, which is what the ticket asked for.
//
// The field commits on Enter and on blur, reverts on Escape, and REFUSES a
// value it cannot parse rather than writing NaN into the graph — a NaN alpha
// propagates into the evaluator and silently zeroes a blend.

import { useEffect, useRef, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { nodeControlClass, parseNodeNumber } from '../../lib/nodeControlClass.js'

const SLIDER_CLASS = nodeControlClass({ extra: 'aos-slider' })
const FIELD_CLASS = nodeControlClass()

export default function NodeValueRow({
  label,
  value,
  min = 0,
  max = 1,
  step = 0.01,
  testId,
  onChange,
  pinId,
  wiredHint,
  accent = '#4ecf7e',
  labelColor,
}) {
  const span = max - min
  const fill = span > 0 ? `${Math.max(0, Math.min(1, (value - min) / span)) * 100}%` : '0%'

  // The entry field is uncontrolled WHILE FOCUSED: the node re-renders on the
  // editor's debug poll, and a controlled input would overwrite half-typed text
  // ("0." becomes "0") on every tick.
  const [draft, setDraft] = useState(null)
  const inputRef = useRef(null)
  const shown = draft ?? (Number.isFinite(value) ? value.toFixed(2) : '')
  useEffect(() => { if (draft !== null && document.activeElement !== inputRef.current) setDraft(null) }, [draft])

  const commit = () => {
    if (draft === null) return
    const n = parseNodeNumber(draft, { min, max })
    if (n !== null) onChange(n)
    setDraft(null) // either way the field snaps back to the graph's real value
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, position: 'relative' }}>
      {pinId && (
        <Handle
          type="target"
          position={Position.Left}
          id={pinId}
          style={{ background: accent, width: 8, height: 8, left: -18, top: '50%', transform: 'translateY(-50%)' }}
        />
      )}
      <span style={{ flex: 'none', color: labelColor ?? (pinId ? accent : '#8a7040'), fontSize: 10 }}>
        {label}{wiredHint ? ' (var)' : ''}
      </span>
      <input
        type="range"
        className={SLIDER_CLASS}
        min={min} max={max} step={step}
        value={Number.isFinite(value) ? value : min}
        data-testid={testId}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fill }}
      />
      <input
        type="text"
        inputMode="decimal"
        className={FIELD_CLASS}
        value={shown}
        aria-label={`${label} value`}
        data-testid={testId ? `${testId}-entry` : undefined}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur() }
          else if (e.key === 'Escape') { e.preventDefault(); setDraft(null); e.currentTarget.blur() }
          // The editors bind Delete/Backspace to "remove selected node"; inside
          // a text field those keys are editing, so they must not bubble.
          else if (e.key === 'Delete' || e.key === 'Backspace') e.stopPropagation()
        }}
        onBlur={commit}
        ref={inputRef}
        style={{
          flex: 'none', width: 38, boxSizing: 'border-box', textAlign: 'right',
          background: '#0a1a1a', border: '1px solid #1a3030', borderRadius: 3,
          color: '#e0c080', fontSize: 10, padding: '1px 3px',
          fontFamily: 'var(--m-mono)', outline: 'none',
        }}
      />
    </div>
  )
}
